/**
 * 文档保存和下载工具模块
 * 用于处理富文本编辑器的保存、另存为、下载DOC等功能
 */

import request from '@/http/request'
import { EventBus } from '@/main.js'

/**
 * 将HTML内容转换为Word文档并下载
 * @param {string} content - HTML内容
 * @param {string} title - 文档标题（用于文件名）
 * @returns {Promise<void>}
 */
export async function downloadAsWord(content, title = '文档') {
  if (!content || !content.trim()) {
    throw new Error('内容为空，无法下载')
  }

  try {
    // 调用后端API获取Word文档URL
    const result = await request('/api/Paper/getWordByPaper', {
      content: content
    })

    if (!result || !result.url) {
      throw new Error('下载失败：返回数据格式错误')
    }

    // 使用改进的下载方法
    await downloadWordFile(result.url, title)
  } catch (error) {
    console.error('下载Word文档失败:', error)
    throw new Error('下载失败：' + (error.message || '网络错误'))
  }
}

/**
 * 下载Word文件（改进版，支持正确的文件格式）
 * @param {string} url - 文件URL
 * @param {string} filename - 文件名（不含扩展名）
 * @returns {Promise<void>}
 */
async function downloadWordFile(url, filename = 'document') {
  try {
    // 确保文件名安全
    const safeFilename = sanitizeFilename(filename)
    
    // 使用fetch获取文件
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // 获取blob数据
    const blob = await response.blob()
    
    // 检查blob类型，确保是正确的Word文档类型
    let finalBlob = blob
    const contentType = response.headers.get('content-type')
    
    // 如果返回的是HTML或文本，需要转换为Word格式
    if (contentType && (contentType.includes('text/html') || contentType.includes('text/plain'))) {
      // 创建一个带有正确MIME类型的新blob
      // 使用application/msword或application/vnd.openxmlformats-officedocument.wordprocessingml.document
      finalBlob = new Blob([blob], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      })
    } else if (contentType && !contentType.includes('word') && !contentType.includes('msword')) {
      // 如果不是Word文档类型，强制设置为Word类型
      finalBlob = new Blob([blob], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      })
    }

    // 创建下载链接
    const blobUrl = window.URL.createObjectURL(finalBlob)
    const anchorElement = document.createElement('a')
    anchorElement.href = blobUrl
    
    // 确保文件扩展名正确
    const finalFilename = safeFilename.endsWith('.doc') || safeFilename.endsWith('.docx') 
      ? safeFilename 
      : `${safeFilename}.doc`
    
    anchorElement.download = finalFilename
    
    // 触发下载
    document.body.appendChild(anchorElement)
    anchorElement.click()
    document.body.removeChild(anchorElement)
    
    // 清理blob URL
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('下载文件失败:', error)
    throw new Error('下载文件失败：' + (error.message || '未知错误'))
  }
}

/**
 * 清理文件名，移除不安全字符
 * @param {string} filename - 原始文件名
 * @returns {string} 清理后的文件名
 */
function sanitizeFilename(filename) {
  // 移除或替换不安全的文件名字符
  return filename
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '') // 移除Windows不允许的字符
    .replace(/\s+/g, '_') // 空格替换为下划线
    .substring(0, 200) // 限制长度
    .trim()
}

/**
 * 保存文档（新建）
 * @param {string} title - 文档标题
 * @param {string} content - 文档内容（HTML）
 * @param {Object} router - Vue Router实例
 * @returns {Promise<Object>} 返回保存结果
 */
export async function saveDocument(title, content, router) {
  if (!title || !title.trim()) {
    throw new Error('标题不能为空')
  }

  const strippedContent = content.replace(/(<([^>]+)>)/gi, '')
  if (!strippedContent.trim()) {
    throw new Error('内容不能为空')
  }

  try {
    const params = {
      paper_title: title,
      paper_url: content,
      paper_type: 'others' // 区分上传的类型 other表示上传的文档和word
    }

    const result = await request('/api/Paper/addPaper', params)

    // 跳转到新文档
    if (router) {
      router.replace({
        query: {
          paperId: result.paper_id,
          t: Date.now() // 时间戳参数确保路由变化，触发文档重新加载
        }
      })
    }

    // 触发文件列表刷新事件
    EventBus.$emit('refreshFileFileList')

    return result
  } catch (error) {
    console.error('保存文档失败:', error)
    
    // 处理重复错误
    const errorMessage = error?.message || '上传错误，请重新提交'
    const isDuplicateError = errorMessage.includes('重复') || 
                            errorMessage.includes('duplicate') || 
                            errorMessage.includes('已存在') || 
                            errorMessage.includes('名称重复')
    
    if (isDuplicateError) {
      throw new Error('文档保存失败：检测到标题重复。请修改文档标题后重新保存。')
    }
    
    throw error
  }
}

/**
 * 更新文档
 * @param {string} paperId - 文档ID
 * @param {string} title - 文档标题
 * @param {string} content - 文档内容（HTML）
 * @returns {Promise<Object>} 返回更新结果
 */
export async function updateDocument(paperId, title, content) {
  if (!paperId) {
    throw new Error('文档ID不能为空')
  }

  if (!title || !title.trim()) {
    throw new Error('标题不能为空')
  }

  try {
    const params = {
      paper_title: title,
      paper_url: content,
      paper_type: 'others',
      paper_id: paperId
    }

    const result = await request('/api/Paper/updatePaper', params)

    // 触发文件列表刷新事件
    EventBus.$emit('refreshFileFileList')

    return result
  } catch (error) {
    console.error('更新文档失败:', error)
    
    // 如果后端返回 code 100，表示内容无变更
    if (error.code === 100) {
      const noChangeError = new Error('编辑无新改动，无需重复保存')
      noChangeError.code = 100
      noChangeError.isNoChange = true
      throw noChangeError
    }
    
    // 如果内容无变更，也算成功
    if (error.message && error.message.includes('无变更')) {
      return { success: true, message: '内容无变更' }
    }
    
    throw error
  }
}

/**
 * 另存为（创建文档副本）
 * @param {string} title - 新文档标题
 * @param {string} content - 文档内容（HTML）
 * @param {Object} router - Vue Router实例
 * @returns {Promise<Object>} 返回保存结果
 */
export async function saveDocumentAs(title, content, router) {
  // 另存为实际上就是创建新文档
  return saveDocument(title, content, router)
}

/**
 * 保存为白板
 * @param {string} title - 白板标题
 * @param {string} content - 内容
 * @param {string} paperId - 来源文档ID（可选）
 * @returns {Promise<Object>} 返回保存结果
 */
export async function saveToDraft(title, content, paperId = '') {
  if (!title || !title.trim()) {
    throw new Error('标题不能为空')
  }

  try {
    const result = await request('/api/Draft/paperGenerateDraft', {
      content: content,
      title: title,
      source_id: paperId || '',
      source: 3
    })

    return result
  } catch (error) {
    console.error('保存白板失败:', error)
    throw new Error('保存白板失败：' + (error.message || '未知错误'))
  }
}

/**
 * 验证内容是否为空
 * @param {string} content - HTML内容
 * @returns {boolean} 内容是否有效
 */
export function validateContent(content) {
  if (!content || !content.trim()) {
    return false
  }

  // 移除HTML标签后检查是否有实际内容
  const strippedContent = content.replace(/(<([^>]+)>)/gi, '')
  return strippedContent.trim().length > 0
}

/**
 * 验证标题是否有效
 * @param {string} title - 标题
 * @returns {boolean} 标题是否有效
 */
export function validateTitle(title) {
  return title && title.trim().length > 0
}

export default {
  downloadAsWord,
  saveDocument,
  updateDocument,
  saveDocumentAs,
  saveToDraft,
  validateContent,
  validateTitle
}

